import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { resolveInitialLanguage } from './shared/i18n/localeRoutes';

// EN namespaces
import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enNavigation from './locales/en/navigation.json';
import enDataUpload from './locales/en/dataUpload.json';
import enDataProcessing from './locales/en/dataProcessing.json';
import enFirstProcessing from './locales/en/firstProcessing.json';
import enDataAdjustments from './locales/en/dataAdjustments.json';
import enDataCloud from './locales/en/dataCloud.json';
import enTraining from './locales/en/training.json';
import enCharts from './locales/en/charts.json';
import enCsvFilter from './locales/en/csvFilter.json';
import enPricing from './locales/en/pricing.json';
import enModals from './locales/en/modals.json';
import enForecastApi from './locales/en/forecastApi.json';
import enLanding from './locales/en/landing.json';
import enLegal from './locales/en/legal.json';
import enPdfReport from './locales/en/pdfReport.json';
import enSessionsTable from './locales/en/sessionsTable.json';
import enNetworkStatus from './locales/en/networkStatus.json';
import enChatbot from './locales/en/chatbot.json';

// DE namespaces
import deCommon from './locales/de/common.json';
import deAuth from './locales/de/auth.json';
import deNavigation from './locales/de/navigation.json';
import deDataUpload from './locales/de/dataUpload.json';
import deDataProcessing from './locales/de/dataProcessing.json';
import deFirstProcessing from './locales/de/firstProcessing.json';
import deDataAdjustments from './locales/de/dataAdjustments.json';
import deDataCloud from './locales/de/dataCloud.json';
import deTraining from './locales/de/training.json';
import deCharts from './locales/de/charts.json';
import deCsvFilter from './locales/de/csvFilter.json';
import dePricing from './locales/de/pricing.json';
import deModals from './locales/de/modals.json';
import deForecastApi from './locales/de/forecastApi.json';
import deLanding from './locales/de/landing.json';
import deLegal from './locales/de/legal.json';
import dePdfReport from './locales/de/pdfReport.json';
import deSessionsTable from './locales/de/sessionsTable.json';
import deNetworkStatus from './locales/de/networkStatus.json';
import deChatbot from './locales/de/chatbot.json';

// Namespace definitions
export const NAMESPACES = [
  'common',
  'auth',
  'navigation',
  'dataUpload',
  'dataProcessing',
  'firstProcessing',
  'dataAdjustments',
  'dataCloud',
  'training',
  'charts',
  'csvFilter',
  'pricing',
  'modals',
  'forecastApi',
  'landing',
  'legal',
  'pdfReport',
  'sessionsTable',
  'networkStatus',
  'chatbot'
] as const;

export type Namespace = (typeof NAMESPACES)[number];

// The URL decides the language on localized routes (`/`, `/en`, `/de`); the
// stored preference decides everywhere else. Resolved here, before init(),
// rather than in a component effect — an effect would render English first and
// then switch, which flashes and can let Googlebot capture English at /de.
let storedLanguage: string | null = null;
try {
  if (typeof window !== 'undefined' && window.localStorage) {
    storedLanguage = localStorage.getItem('language');
  }
} catch (error) {
  console.error('Error accessing localStorage:', error);
}

const savedLanguage = resolveInitialLanguage(
  typeof window !== 'undefined' ? window.location.pathname : '/',
  storedLanguage,
);

// Initialize i18next
i18n.use(initReactI18next).init({
  resources: {
    en: {
      common: enCommon,
      auth: enAuth,
      navigation: enNavigation,
      dataUpload: enDataUpload,
      dataProcessing: enDataProcessing,
      firstProcessing: enFirstProcessing,
      dataAdjustments: enDataAdjustments,
      dataCloud: enDataCloud,
      training: enTraining,
      charts: enCharts,
      csvFilter: enCsvFilter,
      pricing: enPricing,
      modals: enModals,
      forecastApi: enForecastApi,
      landing: enLanding,
      legal: enLegal,
      pdfReport: enPdfReport,
      sessionsTable: enSessionsTable,
      networkStatus: enNetworkStatus,
      chatbot: enChatbot
    },
    de: {
      common: deCommon,
      auth: deAuth,
      navigation: deNavigation,
      dataUpload: deDataUpload,
      dataProcessing: deDataProcessing,
      firstProcessing: deFirstProcessing,
      dataAdjustments: deDataAdjustments,
      dataCloud: deDataCloud,
      training: deTraining,
      charts: deCharts,
      csvFilter: deCsvFilter,
      pricing: dePricing,
      modals: deModals,
      forecastApi: deForecastApi,
      landing: deLanding,
      legal: deLegal,
      pdfReport: dePdfReport,
      sessionsTable: deSessionsTable,
      networkStatus: deNetworkStatus,
      chatbot: deChatbot
    }
  },
  lng: savedLanguage,
  fallbackLng: 'en',
  defaultNS: 'common',
  ns: NAMESPACES,
  interpolation: {
    escapeValue: false // React already safes from XSS
  }
});


// W10-FE6: keep <html lang> attribute in sync with active UI language.
// Project-wide a11y fix — benefits screen readers across all workflows.
if (typeof document !== 'undefined') {
  // Initial sync at module load
  document.documentElement.lang = i18n.language;
  // React to runtime language changes
  i18n.on('languageChanged', (lng: string) => {
    document.documentElement.lang = lng;
  });
}

export default i18n;
